/**
 * IntakyGlobalActivityDrawer — shows ALL recent activities across all intakes.
 * Opens when the "Updated X ago" text is clicked.
 * Receives activities directly from parent — no separate API call needed.
 */
import {
  ArrowPathIcon,
  BellAlertIcon,
  CalendarIcon,
  ChatBubbleLeftIcon,
  ClockIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import React, { useEffect, useRef, useState } from 'react';

import { INTAKY_COLORS, INTAKY_FONT } from './intaky-tokens';

interface Activity {
  id: string;
  intake_id: string;
  activity_type: string;
  headline: string;
  detail: string;
  occurred_at: string;
  metadata: Record<string, string>;
}

interface IntakeNameMap {
  [intakeId: string]: string;
}

interface IntakyGlobalActivityDrawerProps {
  activities: Activity[];
  /** Map of intake_id → client_display for showing names */
  intakeNames: IntakeNameMap;
  onClose: () => void;
}

const ICON_SIZE = 14;

function activityIcon(type: string): React.ReactNode {
  const s = { width: ICON_SIZE, height: ICON_SIZE };
  switch (type) {
    case 'status_changed': return <ArrowPathIcon style={s} />;
    case 'chase_note_added': return <PencilSquareIcon style={s} />;
    case 'loe_sent': return <DocumentArrowUpIcon style={s} />;
    case 'loe_signed': return <SparklesIcon style={s} />;
    case 'callback_scheduled': return <CalendarIcon style={s} />;
    case 'callback_cancelled': return <XMarkIcon style={s} />;
    case 'chase_sms_sent': return <ChatBubbleLeftIcon style={s} />;
    case 'intake_created': return <PlusCircleIcon style={s} />;
    case 'screening_updated': return <ClockIcon style={s} />;
    case 'document_uploaded': return <DocumentArrowUpIcon style={s} />;
    case 'escalated': return <ExclamationTriangleIcon style={s} />;
    default: return <BellAlertIcon style={s} />;
  }
}

const ACTIVITY_LABELS: Record<string, string> = {
  status_changed: 'Status Change',
  chase_note_added: 'Note Added',
  loe_sent: 'LOE Sent',
  loe_signed: 'Retainer Signed',
  callback_scheduled: 'Callback Scheduled',
  callback_cancelled: 'Callback Cancelled',
  chase_sms_sent: 'SMS Sent',
  intake_created: 'Intake Created',
  screening_updated: 'Screening Updated',
  document_uploaded: 'Document Uploaded',
  escalated: 'Escalated',
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const IntakyGlobalActivityDrawer: React.FC<IntakyGlobalActivityDrawerProps> = ({
  activities,
  intakeNames,
  onClose,
}) => {
  const backdropRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const PAGE_SIZE = 30;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Paginate: reveal more when sentinel scrolls into view
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, activities.length));
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [activities.length]);

  const visibleActivities = activities.slice(0, visibleCount);
  const hasMore = visibleCount < activities.length;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(3px)',
        animation: 'intaky-tooltip-in 0.2s ease-out',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 520,
          maxWidth: '90vw',
          background: '#FAFBFC',
          boxShadow: '-8px 0 40px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: INTAKY_FONT.family,
          animation: 'intaky-drawer-slide 0.25s ease-out',
        }}
      >
        {/* Header */}
        <div style={{ background: 'white', borderBottom: `1px solid ${INTAKY_COLORS.border}`, padding: '20px 24px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: INTAKY_COLORS.textPrimary, letterSpacing: '-0.02em' }}>
              All Activity
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: INTAKY_COLORS.textMuted }}>
              {activities.length} recent updates across all leads
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: '#F1F5F9',
              border: 'none',
              cursor: 'pointer',
              width: 28,
              height: 28,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: INTAKY_COLORS.textMuted,
            }}
          >
            <XMarkIcon style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Activity feed */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px 32px' }}>
          {visibleActivities.length === 0 ? (
            <div style={{ padding: '48px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: INTAKY_COLORS.textPrimary, marginBottom: 4 }}>
                No activity yet
              </div>
              <div style={{ fontSize: 12, color: INTAKY_COLORS.textMuted }}>
                Updates will stream here as your team works leads.
              </div>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: 15, top: 8, bottom: 8, width: 1, background: INTAKY_COLORS.border }} />

              {visibleActivities.map((act, idx) => {
                const label = ACTIVITY_LABELS[act.activity_type] ?? act.activity_type;
                const headline = act.headline.replace(/\bAuto\s+/gi, '');
                const detail = (act.detail || '').replace(/\bAuto\s+/gi, '');
                const clientName = intakeNames[act.intake_id] ?? '';
                const isFirst = idx === 0;

                return (
                  <div
                    key={act.id}
                    style={{
                      position: 'relative',
                      paddingLeft: 42,
                      paddingBottom: 16,
                      animation: isFirst ? 'intaky-row-in 0.3s ease-out' : undefined,
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: 6,
                        top: 4,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: isFirst ? '#DCFCE7' : '#F1F5F9',
                        border: `2px solid ${isFirst ? '#22C55E' : INTAKY_COLORS.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isFirst ? '#15803D' : INTAKY_COLORS.textMuted,
                        zIndex: 1,
                      }}
                    >
                      {activityIcon(act.activity_type)}
                    </div>

                    <div
                      style={{
                        background: 'white',
                        borderRadius: 10,
                        padding: '10px 14px',
                        border: `1px solid ${INTAKY_COLORS.border}`,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: INTAKY_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          {label}
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 500, color: INTAKY_COLORS.textMuted, whiteSpace: 'nowrap' }}>
                          {formatTime(act.occurred_at)}
                        </span>
                      </div>
                      {clientName && (
                        <div style={{ fontSize: 12, fontWeight: 700, color: INTAKY_COLORS.textPrimary, marginBottom: 2 }}>
                          {clientName}
                        </div>
                      )}
                      <div style={{ fontSize: 13, fontWeight: 600, color: clientName ? INTAKY_COLORS.textSecondary : INTAKY_COLORS.textPrimary, lineHeight: 1.4 }}>
                        {headline}
                      </div>
                      {detail && (
                        <div style={{ marginTop: 6, fontSize: 12, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#F8FAFC', padding: '8px 12px', borderRadius: 8 }}>
                          {detail}
                        </div>
                      )}
                      {Object.keys(act.metadata).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                          {Object.entries(act.metadata).map(([k, v]) => (
                            <span key={k} style={{ fontSize: 10, fontWeight: 600, color: INTAKY_COLORS.textMuted, background: '#F1F5F9', padding: '2px 8px', borderRadius: 4 }}>
                              {k.replace(/_/g, ' ')}: {v}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Scroll sentinel */}
              <div ref={sentinelRef} style={{ height: 1 }} />
              {hasMore && (
                <div style={{ padding: '12px 0', textAlign: 'center', fontSize: 11, color: INTAKY_COLORS.textMuted }}>
                  Showing {visibleCount} of {activities.length} updates
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
